import { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { apiService } from '@/service/api'
import { toast } from 'sonner'
import { resolveGraphOpen } from '@/utils/resolveGraphOpen'
import { Search, Database, Loader2, ExternalLink, FolderOpen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import AppSidebar from '@/components/AppSidebar'
import DataSidebar from '@/components/DataSidebar'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { Input } from '@/components/ui/input'
import type { HoverInfo } from './KnowledgeGraphCanvas'
import KnowledgeGraphD3Canvas, { type D3CanvasHandle } from './KnowledgeGraphD3Canvas'
import EditorWrapper from '@/routes/GraphEditor/EditorWrapper'
import { EDITOR_MODE } from '@/routes/GraphEditor'
import TypesPanel from './TypesPanel'
import ZoomControls from './ZoomControls'
import MinimapPanel from './MinimapPanel'
import NodeHoverCard from './NodeHoverCard'
import AlertView from './AlertView'
import NodeDetailsPanel, { type NodeConnectionLink } from './NodeDetailsPanel'
import { useKnowledgeGraphData } from './useKnowledgeGraphData'
import { treeViewGraph } from './treeViewGraph'
import RetinaOnlyEmptyState from './RetinaOnlyEmptyState'
import { useTenantEntitlements } from '@/hooks/useTenantEntitlements'
import { useOrganizationDirectory } from '@/hooks/useOrganizationDirectory'
import type { AlertItem } from './mockAlerts'
import type { MockNodeType } from './types'
import { DEFAULT_GRAPH_LAYOUT, type GraphLayout } from './layoutOptions'
import DisplaySettingsPopover from './DisplaySettingsPopover'
import { DEFAULT_DISPLAY_SETTINGS, type DisplaySettings } from './displaySettings'
import FilterPopover from './FilterPopover'
import FilterChips from './FilterChips'
import { EMPTY_KG_FILTERS, excludedNodeIds, type KgFilters } from './filters'
import { filterGraphForSearch, searchKeepTypes, TEST_ORPHAN_ID, TEST_ORPHAN_PARAM, TEST_ORPHAN_TAGS, withTestOrphan } from './search'
import {
  DATA_CATALOG_ROUTE,
  embeddedPreviewRoute,
  externalPreviewRoute,
  primeDataCatalogSearch,
  type PreviewOverlay,
} from './previewRoutes'

type ViewMode = 'graph' | 'alert'

/** How long the search field is quiet before the graph is narrowed to it. */
const SEARCH_DEBOUNCE_MS = 150
/** A tag's identity, the way Tag Mapping compares them: upper-cased, nothing but letters and digits. */
const normTag = (tag: string): string => tag.toUpperCase().replace(/[^A-Z0-9]/g, '')

/** The same gate Settings uses for its internal-only tabs (`SettingsV2.tsx`); gates the test orphan. */
const isInternalEmail = (email: string | undefined): boolean =>
  !!email && (email.endsWith('@intuigence.ai') || email.endsWith('@intuigence.com'))

const LAYOUT_OPTIONS: { value: GraphLayout; labelKey: string; defaultLabel: string }[] = [
  { value: 'radial', labelKey: 'knowledge_graph:toolbar.radial_view', defaultLabel: 'Radial View' },
  { value: 'tree', labelKey: 'knowledge_graph:toolbar.tree_view', defaultLabel: 'Tree View' },
]

const KnowledgeGraphPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // UI-45: no control switches this any more — the Graph/Alert strip is gone.
  // Kept (rather than inlining 'graph' at both read sites) so restoring the
  // tabs is re-adding the triggers and a setter, not reconstructing the
  // branches they drive.
  const [view] = useState<ViewMode>('graph')
  const [search, setSearch] = useState('')
  // DSGN-63: entity labels, edge labels, orphans and line thickness, behind
  // the toolbar gear. Defaults per the designer's notes on the Display frames.
  const [display, setDisplay] = useState<DisplaySettings>(DEFAULT_DISPLAY_SETTINGS)
  // DSGN-63: the page always OPENS in Radial, the designed default; Tree is
  // for the visit. (It used to be remembered per browser, so one click on
  // Tree View made every later open a tree — the reporter's complaint.)
  const [layout, setLayout] = useState<GraphLayout>(DEFAULT_GRAPH_LAYOUT)

  /**
   * The segmented control's active pill is ONE element that slides, rather than
   * a background on each button. Per-button backgrounds can only cross-fade —
   * and `transition-colors` does not cover `box-shadow`, so the old pill's
   * shadow popped on instantly while its colours faded, which is what read as
   * unsmooth. Measuring the active button and translating a single pill gives
   * the motion a segmented control is supposed to have.
   */
  const layoutTrackRef = useRef<HTMLDivElement>(null)
  const layoutBtnRefs = useRef<Partial<Record<GraphLayout, HTMLButtonElement | null>>>({})
  const [layoutPill, setLayoutPill] = useState<{ left: number; width: number } | null>(null)
  // Suppress the transition until the pill has been placed once, or it would
  // animate in from the left edge on first paint.
  const layoutPillReady = useRef(false)

  useLayoutEffect(() => {
    const measure = (): void => {
      const btn = layoutBtnRefs.current[layout]
      if (!btn) return
      setLayoutPill((prev) => {
        const next = { left: btn.offsetLeft, width: btn.offsetWidth }
        if (prev && prev.left === next.left && prev.width === next.width) return prev
        return next
      })
    }
    measure()
    // The labels are translated and the font loads async, so the button widths
    // can change after the first measurement.
    const track = layoutTrackRef.current
    if (!track || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(track)
    for (const el of Object.values(layoutBtnRefs.current)) if (el) ro.observe(el)
    return () => ro.disconnect()
  }, [layout, t])

  useEffect(() => {
    if (layoutPill) layoutPillReady.current = true
  }, [layoutPill])
  const handleLayoutChange = useCallback((next: GraphLayout) => setLayout(next), [])
  // DSGN-63: the Filter popover's facets. Entity type feeds the canvas (and
  // the legend toggles the same list); Uploaded by becomes the set of node ids
  // the canvas dims. Empty lists mean "everything".
  const [filters, setFilters] = useState<KgFilters>(EMPTY_KG_FILTERS)
  const enabledTypes = useMemo(() => new Set<MockNodeType>(filters.types), [filters.types])
  const [selectedAlertId, setSelectedAlertId] = useState<string | undefined>()
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [hoveredNode, setHoveredNode] = useState<HoverInfo | null>(null)

  // The chat (BoardChatPanelV2 → ChatInterface → sendUserMessage) requires a
  // context_id from the store; without one, every send is a silent no-op.
  // The KG page has no board of its own, so mirror BoardListV2's auto-select:
  // pick the most recent user board. FS-553 removed the Master Board, so when
  // the user has no boards we leave context_id unset rather than auto-creating
  // one on a passive page visit — a board is created from an explicit action.
  const chatContextId = useAppStore((s) => s.context_id)
  const setContextId = useAppStore((s) => s.setContextId)
  const boardList = useAppStore((s) => s.boardList)
  // Clear "Use Entire Data Catalog" when this route is left, the same contract
  // BoardCanvas holds. This route hosts BoardChatPanelV2 too, and the reset used
  // to ride on that panel's unmount — moving it to the route (so an empty board's
  // entry card could set the scope before the panel exists) would otherwise leave
  // a scope ticked here switched on for every route the user visits next.
  const setEntireDataCatalog = useAppStore((s) => s.setEntireDataCatalog)
  useEffect(() => {
    return () => setEntireDataCatalog(false)
  }, [setEntireDataCatalog])
  const loadBoardList = useAppStore((s) => s.loadBoardList)
  useEffect(() => {
    if (chatContextId) return
    let cancelled = false
    void (async () => {
      if (boardList.length === 0) await loadBoardList()
      if (cancelled) return
      const fresh = useAppStore.getState()
      if (fresh.context_id) return
      if (fresh.boardList.length > 0) {
        const sorted = [...fresh.boardList].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        setContextId(sorted[0].id)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [chatContextId, boardList.length, loadBoardList, setContextId])
  const canvasRef = useRef<D3CanvasHandle>(null)
  // The page draws the knowledge graph the AI answers from (the tenant's
  // raw/ tables); the catalog hierarchy was retired with it (product call,
  // 2026-10-07). Internal users get the test orphan below.
  const userEmail = useAppStore((s) => s.user?.email)
  const isInternalUser = isInternalEmail(userEmail)
  // Uploader names: the endpoint's own when the backend resolved them, else
  // the organization directory — the same roster the Data Catalog names
  // uploaders from — so Uploaded by works wherever the Keycloak lookup fails.
  const directory = useOrganizationDirectory()
  const resolveUploader = useCallback(
    (userId: string) => directory.find((u) => u.user_id === userId)?.name ?? undefined,
    [directory],
  )
  // The graph's root node is the tenant, so it is labelled with the tenant's
  // own name. `displayName` is what was typed at org creation; `name` is the
  // Keycloak slug and the fallback for orgs created before displayName existed
  // — the same pair, in the same order, that the account menu shows.
  const organizationsRaw = useAppStore((s) => s.organizations)
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const tenantName = useMemo(() => {
    const orgs = Array.isArray(organizationsRaw) ? organizationsRaw : []
    const active = orgs.find((o) => o.id === activeOrganizationId) ?? orgs[0]
    return active?.displayName ?? active?.name ?? undefined
  }, [organizationsRaw, activeOrganizationId])
  const { data: graph, loading: graphLoading, error: graphError, empty: graphEmpty } = useKnowledgeGraphData(resolveUploader, tenantName)
  // Internal testing: `?kg_orphan=1` adds a document that belongs to nothing,
  // so the orphan handling can be tried without filtering a site away first.
  const wantTestOrphan = useMemo(() => {
    try {
      return isInternalUser && new URLSearchParams(window.location.search).has(TEST_ORPHAN_PARAM)
    } catch {
      return false
    }
  }, [isInternalUser])
  const baseGraph = useMemo(() => (wantTestOrphan ? withTestOrphan(graph) : graph), [graph, wantTestOrphan])
  const filteredOutIds = useMemo(() => excludedNodeIds(baseGraph.nodes, filters), [baseGraph.nodes, filters])
  // The search narrows the GRAPH, as the design draws it: only the matches and
  // their ancestors are handed to the canvas, which lays that subgraph out on
  // its own, and the legend lists the types left. Debounced, because the
  // canvas rebuilds for a new node set and a keystroke every 50ms should not
  // replay the entrance each time.
  const [searchApplied, setSearchApplied] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setSearchApplied(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])
  const shownGraph = useMemo(() => filterGraphForSearch(baseGraph, searchApplied), [baseGraph, searchApplied])
  const searching = searchApplied.trim() !== ''
  // Tree View stacks the sheets under their Area → System and draws one
  // cross link per pair of sheets (treeViewGraph); Radial View is unchanged.
  const canvasGraph = useMemo(() => (layout === 'tree' ? treeViewGraph(shownGraph) : shownGraph), [layout, shownGraph])
  const searchTypes = useMemo(() => searchKeepTypes(baseGraph.nodes, searchApplied), [baseGraph.nodes, searchApplied])
  // A selected node the search removed has no card to stand behind.
  useEffect(() => {
    if (selectedNodeId && !canvasGraph.nodes.some((n) => n.id === selectedNodeId)) setSelectedNodeId(null)
  }, [canvasGraph, selectedNodeId])
  // Opening Cortex: a Retina-only tenant owns documents but not the graph layer.
  const entitlements = useTenantEntitlements()

  const selectedNode = useMemo(
    () =>
      baseGraph.nodes.find((n) => n.id === selectedNodeId) ??
      // A Tree View group (area / system) exists only in the canvas graph.
      canvasGraph.nodes.find((n) => n.id === selectedNodeId) ??
      null,
    [baseGraph.nodes, canvasGraph.nodes, selectedNodeId],
  )
  // Mapped Tag chips (the design's Node Details): the historian tags matched
  // to this node, from Tag Mapping. A sheet asks for the tags that occur on
  // the document it was parsed from; an equipment asks for its own tag and
  // keeps only that tag's matches. A failed or stale fetch leaves the section
  // out rather than showing another node's list.
  const [mappedTags, setMappedTags] = useState<{ nodeId: string; tags: string[] } | null>(null)
  // Keyed on the id and document id (strings), not the node object: the graph
  // is rebuilt on a search and the node would be a new object each time.
  const selectedDocumentId = selectedNode?.documentId
  useEffect(() => {
    const nodeId = selectedNodeId
    if (!nodeId || nodeId === TEST_ORPHAN_ID) {
      setMappedTags(null)
      return
    }
    const equipmentTag = nodeId.startsWith('equip:') ? nodeId.slice('equip:'.length) : null
    const documentId = selectedDocumentId
    if (!equipmentTag && !documentId) {
      setMappedTags(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const resp = equipmentTag
          ? await apiService.getMatchedTags({ search: equipmentTag, limit: 50 })
          : await apiService.getMatchedTags({ documentId, limit: 200 })
        if (cancelled) return
        const seen = new Set<string>()
        for (const group of resp.groups) {
          if (equipmentTag && normTag(group.tag) !== normTag(equipmentTag)) continue
          for (const m of group.matches) if (m.tag) seen.add(m.tag)
        }
        setMappedTags({ nodeId, tags: [...seen] })
      } catch {
        if (!cancelled) setMappedTags(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedNodeId, selectedDocumentId])
  const selectedNodeTags =
    selectedNodeId === TEST_ORPHAN_ID ? TEST_ORPHAN_TAGS : mappedTags && mappedTags.nodeId === selectedNodeId ? mappedTags.tags : undefined

  const selectedNodeConnections = useMemo(() => {
    if (!selectedNodeId) return []
    const labelById = new Map(baseGraph.nodes.map((n) => [n.id, n.label]))
    const sourceLabel = labelById.get(selectedNodeId) ?? selectedNodeId
    const result: NodeConnectionLink[] = []
    for (const e of baseGraph.edges) {
      // Both directions, named source → target as the design's chips are
      // ("Texaco → Texaco Houston - Site C" on the site's own card): a leaf
      // document's one link is the incoming one from its site, and listing
      // outgoing links alone left every leaf reading "No outgoing connections".
      let otherId: string | null = null
      let label: string | null = null
      if (e.source === selectedNodeId) {
        otherId = e.target
        label = `${sourceLabel} → ${labelById.get(otherId) ?? otherId}`
      } else if (e.target === selectedNodeId) {
        otherId = e.source
        label = `${labelById.get(otherId) ?? otherId} → ${sourceLabel}`
      }
      if (!otherId || !label) continue
      result.push({ id: e.id, label, targetId: otherId })
    }
    return result
  }, [baseGraph.edges, baseGraph.nodes, selectedNodeId])

  /*
   * The hover card says the node's name and nothing else.
   *
   * It used to also carry an alert, dealt here from the MOCK_ALERTS literal:
   * any alert without a matching `nodeId` was attached to whichever leaf node
   * came next in array order. Three of the four had no nodeId, so a made-up
   * reading ("Controller FIC-100 — Flow at 2,550 bpm, IOW max 2,600") rendered
   * over a real, unrelated equipment tag, and moved to a different tag as soon
   * as a filter changed the node order. There is no alerts API behind it, so
   * there is nothing true to show; the fabricated version is gone rather than
   * left looking live.
   */
  const hoveredNodeInfo = useMemo(() => {
    if (!hoveredNode) return null
    const node = baseGraph.nodes.find((n) => n.id === hoveredNode.id) ?? canvasGraph.nodes.find((n) => n.id === hoveredNode.id)
    if (!node) return null
    return { label: node.label }
  }, [hoveredNode, baseGraph.nodes, canvasGraph.nodes])

  const handleToggleType = useCallback((type: MockNodeType) => {
    setFilters((prev) => {
      // From "everything" the first click means "everything but this one".
      if (prev.types.length === 0) {
        const all: MockNodeType[] = [
          'site',
          'operating_area',
          'operation_unit',
          'system',
          'functional_location',
          'components',
          'documents',
          'user_artifacts',
        ]
        return { ...prev, types: all.filter((t) => t !== type) }
      }
      const types = prev.types.includes(type) ? prev.types.filter((t) => t !== type) : [...prev.types, type]
      return { ...prev, types }
    })
  }, [])

  const handleAlertClick = useCallback((alert: AlertItem) => {
    setSelectedAlertId(alert.id)
  }, [])

  /*
   * A connection chip highlights that edge in place (DSGN-63 design): the chip
   * goes teal, the edge and its far end go teal on the canvas, and the card
   * stays on the node it was opened for. It used to jump to the far node.
   * Keyed by the node it was picked on, so picking another node clears it.
   */
  const [pickedConnection, setPickedConnection] = useState<{ nodeId: string; connectionId: string } | null>(null)
  const handleNodeSelect = useCallback((nodeId: string | null) => {
    setSelectedNodeId(nodeId)
    // A click on the canvas (even on the same node) drops the picked connection.
    setPickedConnection(null)
  }, [])

  const selectedConnectionId = pickedConnection && pickedConnection.nodeId === selectedNodeId ? pickedConnection.connectionId : null
  const selectedLink = useMemo(() => {
    if (!selectedNodeId || !selectedConnectionId) return null
    const c = selectedNodeConnections.find((x) => x.id === selectedConnectionId)
    return c ? { source: selectedNodeId, target: c.targetId } : null
  }, [selectedConnectionId, selectedNodeConnections, selectedNodeId])
  const handleConnectionSelect = useCallback(
    (connectionId: string) => {
      if (!selectedNodeId) return
      setPickedConnection((prev) =>
        prev && prev.nodeId === selectedNodeId && prev.connectionId === connectionId ? null : { nodeId: selectedNodeId, connectionId },
      )
    },
    [selectedNodeId],
  )

  // Floating preview over the canvas: the P&ID editor, in-tree (no iframe —
  // same React tree, no app bootstrap). A sheet knows the document it was
  // parsed from; equipment has no file of its own, so no button.
  const [previewOverlay, setPreviewOverlay] = useState<PreviewOverlay | null>(null)
  // True until the viewer signals it is ready; drives the spinner over the dialog body.
  const [previewLoading, setPreviewLoading] = useState(false)
  // Monotonic token guarding the async graph resolution: a resolution whose
  // id is stale (the dialog was dismissed, or a newer node opened, while
  // getDocument was in flight) is ignored.
  const previewReqRef = useRef(0)
  // Failsafe: cap the spinner so it cannot spin forever when a viewer never
  // signals; the inner viewer shows its own loading state after that.
  useEffect(() => {
    if (!previewOverlay) return
    const timer = setTimeout(() => setPreviewLoading(false), 8000)
    return () => clearTimeout(timer)
  }, [previewOverlay])

  const handleOpenPreview = useMemo(() => {
    if (!selectedNode) return undefined
    // The test orphan has the card's button; there is no file behind it.
    if (selectedNode.id === TEST_ORPHAN_ID) {
      return () => toast.info('This is a test node — there is no file to preview.')
    }
    const docId = selectedNode.documentId
    if (!docId) return undefined
    const title = selectedNode.label
    return async () => {
      const reqId = ++previewReqRef.current
      setPreviewLoading(true)
      // A sheet's node carries the DOCUMENT id; the editor needs the GRAPH id.
      // Resolve it the way the Data Catalog does, so the preview opens the same
      // graph instead of a misleading "still being processed" toast.
      try {
        const doc = await apiService.getDocument(docId)
        if (reqId !== previewReqRef.current) return
        const { graphId, pnidPages } = resolveGraphOpen(doc.properties)
        if (!graphId) {
          setPreviewLoading(false)
          toast.info(
            t('data_catalog:graph_not_ready', 'Graph is still being processed. Please wait a moment and try again.')
          )
          return
        }
        setPreviewOverlay({ kind: 'graph', id: graphId, documentId: docId, pnidPages, title })
      } catch (err) {
        if (reqId !== previewReqRef.current) return
        setPreviewLoading(false)
        // A missing / not-yet-ready document (404) is the expected "still
        // processing" case; a real outage is said as one.
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status !== undefined && status !== 404) {
          toast.error(
            t('data_catalog:graph_open_failed', "Couldn't open this graph — please try again.")
          )
        } else {
          toast.info(
            t('data_catalog:graph_not_ready', 'Graph is still being processed. Please wait a moment and try again.')
          )
        }
      }
    }
  }, [selectedNode, t])

  const previewExternalRoute = previewOverlay ? externalPreviewRoute(previewOverlay) : ''
  const previewIframeRoute = previewOverlay ? embeddedPreviewRoute(previewOverlay) : ''

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <DataSidebar />

      <div className="flex flex-1 flex-col min-w-0">
        {/* Top row: title (left) + tabs (center) */}
        {/* The title carries the header on its own — no icon — and sits on the
            same 18px gutter as the toolbar below, so it lines up with Search. */}
        <header className="flex h-[60px] shrink-0 items-center border-b border-border bg-background px-6 dark:bg-dark-card">
          <div className="flex items-center flex-1">
            <span className="text-sm font-medium text-foreground">
              {t('knowledge_graph:title', { defaultValue: 'Knowledge Graph' })}
            </span>
          </div>
          {/* UI-45 (Pegah): "What's this vs that? Confusing" — her screenshot
              rings the Alert tab and the notification bell. Both were disabled
              "Coming soon" placeholders for the same unbuilt alert feature, so
              the page offered two dead doors to it with nothing to tell them
              apart, one wearing a red count badge that made it look live.

              With Alert gone the strip held a single tab, which switches to the
              view already on screen — chrome that reads as a control and is
              not one. So the strip goes too, and with it the flex-1 spacer that
              only existed to centre it.

              None of the machinery is removed: the `alert` view mode,
              AlertView and the per-node alert hover cards all remain, so
              restoring this is re-adding the triggers, not rebuilding. */}
        </header>

        {/* Sub-toolbar (Graph only). DSGN-63 Phase 1: the inline entity-type
            select and the edge-label switch move behind a Filter button and a
            display-settings gear, matching the Figma toolbar. Each popover
            holds the control it replaced, so nothing is lost while the fuller
            filter panel and display options land in the follow-up PRs. */}
        {view === 'graph' && (
          <div className="flex h-[62px] items-center justify-between gap-3 border-b border-border bg-background px-4 shrink-0">
            {/* The design's toolbar at its true size (the Figma export is 2x, so
                every measure is half the image): 36px white controls with a
                neutral-200 border and a 6px radius, 16px in from the edges and
                10px apart. The search field is 237px wide, its icon 14px in and
                its 14px placeholder 36px in. */}
            <div className="flex items-center gap-2.5">
              <div className="relative w-[237px]">
                <Search className="size-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={String(t('knowledge_graph:toolbar.search', { defaultValue: 'Search' }))}
                  className="h-9 rounded-md border-neutral-200 bg-background pl-9 pr-3 text-sm shadow-none placeholder:text-muted-foreground md:text-sm dark:border-neutral-700"
                />
              </div>
              <FilterPopover nodes={baseGraph.nodes} filters={filters} onChange={setFilters} />
            </div>
            <div className="flex items-center gap-4">
              <div
                ref={layoutTrackRef}
                role="group"
                aria-label={String(t('knowledge_graph:toolbar.layout', { defaultValue: 'Graph layout' }))}
                // The larger segmented control, with the track hugging the active
                // pill: 2px of padding rather than 4px. Track neutral-100, the
                // active pill white with a neutral-200 edge — the three greys
                // measured off the design.
                //
                // The radii are CONCENTRIC: the pill's is the track's minus the
                // 2px padding (8 - 2 = 6), so the two curves stay parallel. Any
                // other pairing makes the pill read as bulging out of the track
                // at the corners, which is worse the more of the track it fills.
                className="relative flex h-9 items-center rounded-lg bg-neutral-100 p-0.5 dark:bg-neutral-800"
              >
                {layoutPill && (
                  <span
                    aria-hidden
                    className={[
                      'absolute left-0 top-0.5 h-8 rounded-md border border-neutral-200 bg-white shadow-sm',
                      'dark:border-neutral-600 dark:bg-neutral-900',
                      layoutPillReady.current
                        ? 'transition-[transform,width] duration-100 ease-out motion-reduce:transition-none'
                        : '',
                    ].join(' ')}
                    style={{ transform: `translateX(${layoutPill.left}px)`, width: layoutPill.width }}
                  />
                )}
                {LAYOUT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    ref={(el) => { layoutBtnRefs.current[opt.value] = el }}
                    aria-pressed={layout === opt.value}
                    onClick={() => handleLayoutChange(opt.value)}
                    className={[
                      'relative z-10 h-8 rounded-md px-3 text-sm font-medium transition-colors duration-100',
                      layout === opt.value
                        ? 'text-neutral-900 dark:text-neutral-50'
                        : 'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100',
                    ].join(' ')}
                  >
                    {t(opt.labelKey, { defaultValue: opt.defaultLabel })}
                  </button>
                ))}
              </div>
              <DisplaySettingsPopover settings={display} onChange={setDisplay} />
            </div>
          </div>
        )}

        {view === 'graph' && <FilterChips filters={filters} onChange={setFilters} />}

        {/* Canvas / Alert + chat panel */}
        <div className="flex flex-1 min-h-0">
          <div className="relative flex-1 min-w-0 overflow-hidden bg-muted/30">
            {view === 'graph' ? (
              graphLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {t('knowledge_graph:loading', { defaultValue: 'Loading knowledge graph…' })}
                </div>
              ) : /* Entitlement BEFORE the error. A Retina-only tenant still fires the
                     graph request, so when that request fails or times out the page said
                     "Failed to load graph" — presenting a tenant that has not bought the
                     graph with a fault, and inviting a support ticket for working software. */
              entitlements.hasUkg === false ? (
                <RetinaOnlyEmptyState />
              ) : graphError ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-center px-6">
                  <p className="text-sm font-semibold text-foreground">
                    {t('knowledge_graph:error.title', { defaultValue: 'Failed to load graph' })}
                  </p>
                  <p className="text-xs text-muted-foreground max-w-[400px]">{graphError}</p>
                </div>
              ) : graphEmpty ? (
                <div className="flex h-full flex-col items-center justify-center px-6">
                  <div className="flex flex-col items-center gap-4 rounded-lg p-6">
                    <div className="flex flex-col items-center gap-4">
                      <div className="flex size-8 items-center justify-center rounded-lg bg-muted">
                        <Database className="size-4 text-foreground" />
                      </div>
                      <div className="flex flex-col items-center gap-2 text-center">
                        <p className="text-sm font-medium text-foreground">
                          {t('knowledge_graph:empty.title', {
                            defaultValue: 'Nothing to visualize yet',
                          })}
                        </p>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {t('knowledge_graph:empty.subtitle', {
                            defaultValue: 'Upload your first document to start building the graph.',
                          })}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => navigate('/data-catalog')}>
                      {t('knowledge_graph:empty.cta', { defaultValue: 'Go to Data Catalog' })}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <KnowledgeGraphD3Canvas
                    layout={layout}
                    showEntityLabels={display.entityLabels}
                    showOrphans={display.orphans}
                    keepUnlinked={searching}
                    lineWidth={display.lineWidth}
                    filteredOutIds={filteredOutIds}
                    ref={canvasRef}
                    nodes={canvasGraph.nodes}
                    edges={canvasGraph.edges}
                    enabledTypes={enabledTypes}
                    showEdgeLabels={display.edgeLabels}
                    onNodeSelect={handleNodeSelect}
                    selectedNodeId={selectedNodeId}
                    selectedLink={selectedLink}
                    onNodeHover={setHoveredNode}
                  />
                  {/* DSGN-63: the legend is a key (Entity / Documents), not a tally. */}
                  <TypesPanel
                    nodes={shownGraph.nodes}
                    enabledTypes={enabledTypes}
                    onToggle={handleToggleType}
                    presentTypes={searchTypes}
                    hideCounts
                    title={String(t('knowledge_graph:types.entity', { defaultValue: 'Entity' }))}
                  />
                  {/* One card, as the design draws it (Figma "2. Radial"): the
                      thumbnail fills the card's width, a divider under it, and
                      the zoom row at the same width with no chrome of its own. */}
                  <div className="absolute left-3 bottom-3 z-10 w-[150px] overflow-hidden rounded-xl border border-border bg-background shadow-md dark:bg-dark-card">
                    <div className="p-2">
                      <MinimapPanel canvasRef={canvasRef} embedded selectedNodeId={selectedNodeId} />
                    </div>
                    <div className="border-t border-border px-1.5 py-0.5">
                      <ZoomControls canvasRef={canvasRef} followCanvasFloor />
                    </div>
                  </div>
                  {hoveredNode && hoveredNodeInfo && (
                    <NodeHoverCard
                      label={hoveredNodeInfo.label}
                      x={hoveredNode.x}
                      y={hoveredNode.y}
                    />
                  )}
                  {/* DSGN-63 design: the details are a floating card over the canvas. */}
                  {selectedNode && (
                    <NodeDetailsPanel
                      node={selectedNode}
                      connections={selectedNodeConnections}
                      onClose={() => setSelectedNodeId(null)}
                      onConnectionSelect={handleConnectionSelect}
                      selectedConnectionId={selectedConnectionId}
                      onOpenPreview={handleOpenPreview}
                      tags={selectedNodeTags}
                      tagsLabel={String(t('knowledge_graph:node_details.mapped_tag', { defaultValue: 'Mapped Tag' }))}
                      plainAttributes
                      floating
                    />
                  )}
                </>
              )
            ) : (
              <AlertView
                search={search}
                onSearchChange={setSearch}
                onAlertClick={handleAlertClick}
                selectedAlertId={selectedAlertId}
              />
            )}
            {/* The floating AI-chat launcher is gone, and the panel with it:
                that button was its only trigger, so keeping the panel would
                have left code nothing could reach. */}
          </div>

        </div>
      </div>

      <Dialog
        open={previewOverlay !== null}
        onOpenChange={(open) => {
          if (!open) {
            // Invalidate any in-flight graph resolution so it can't reopen the
            // dialog after the user closed it.
            previewReqRef.current++
            setPreviewOverlay(null)
          }
        }}
      >
        <DialogContent
          className="max-w-none w-[85vw] h-[85vh] p-0 gap-0 overflow-hidden flex flex-col"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b shrink-0 min-w-0">
            <DialogTitle asChild>
              <span className="contents">
                <FileNameWithCopy
                  name={previewOverlay?.title ?? ''}
                  className="text-sm font-medium"
                />
              </span>
            </DialogTitle>
            {/* The designer's File Preview note: this dialog only previews.
                Search or any other action happens in the Data Catalog, and the
                file's own page opens in a new tab. */}
            <div className="ms-auto me-8 flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  primeDataCatalogSearch(previewOverlay?.title ?? '')
                  navigate(DATA_CATALOG_ROUTE)
                }}
              >
                <FolderOpen className="size-3.5" />
                {t('knowledge_graph:preview.open_in_data_catalog', { defaultValue: 'Open in Data Catalog' })}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => window.open(previewExternalRoute, '_blank', 'noopener')}
              >
                <ExternalLink className="size-3.5" />
                {t('knowledge_graph:preview.open_in_new_tab', { defaultValue: 'Open in new tab' })}
              </Button>
            </div>
          </div>
          <div className="relative flex-1 min-h-0 overflow-hidden bg-background">
            {previewOverlay?.kind === 'graph' && (
              // In-tree mount of the P&ID editor — no iframe means no React
              // bootstrap, no auth round-trip, no chunk re-parse. Loads in
              // the same render cycle as the dialog open.
              <EditorWrapper
                key={previewOverlay.id}
                graphId={previewOverlay.id}
                documentId={previewOverlay.documentId}
                isReadOnly={true}
                activeTab={EDITOR_MODE}
                initialZoom={1}
                onDocumentNameResolved={() => setPreviewLoading(false)}
              />
            )}
            {previewOverlay && previewOverlay.kind !== 'graph' && (
              <iframe
                key={previewIframeRoute}
                src={previewIframeRoute}
                title={previewOverlay.title}
                onLoad={() => setPreviewLoading(false)}
                onError={() => setPreviewLoading(false)}
                className="w-full h-full border-0 bg-background"
              />
            )}
            {previewLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm z-10 pointer-events-none">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default KnowledgeGraphPage
